/**
 * AlbumDetail — 앨범 상세 + 이미지 갤러리
 */

import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router'
import { ArrowLeft, Camera } from 'lucide-react'
import { useAlbum } from '../../hooks/useAlbums.js'
import { Badge, Button, Spinner, EmptyState, ImageGallery } from '../../components/ui/index.js'
import type { GalleryImage } from '../../components/ui/index.js'
import styles from './AlbumDetail.module.css'

export function AlbumDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { album, isLoading, error } = useAlbum(id ?? null)

  const galleryImages = useMemo<GalleryImage[]>(() => {
    const fromGen = (album?.generations ?? []).flatMap(g =>
      (g.generatedUrls ?? []).map((url, i) => ({ src: url, alt: `${album!.name} ${i + 1}` }))
    )
    if (fromGen.length > 0) return fromGen
    return album?.images.map((img, i) => ({ src: img.url, alt: `${album.name} ${i + 1}` })) ?? []
  }, [album])

  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingRow}><Spinner size="lg" /></div>
      </div>
    )
  }

  if (error || !album) {
    return (
      <div className={styles.root}>
        <EmptyState
          title="촬영을 찾을 수 없습니다"
          description={error ?? '존재하지 않는 촬영이거나 삭제된 촬영입니다'}
          action={
            <Button onClick={() => navigate('/studio/ai')}>돌아가기</Button>
          }
        />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      {/* 헤더 */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={() => navigate('/studio/ai')}>
            <ArrowLeft size={20} />
          </button>
          <h1 className={styles.albumTitle}>{album.name}</h1>
          <Badge color="gray">{galleryImages.length}장</Badge>
        </div>
        <span className={styles.meta}>{album.status === 'draft' ? '생성 중' : '완료'}</span>
      </div>

      {/* 이미지 그리드 */}
      {galleryImages.length === 0 ? (
        <EmptyState
          icon={<Camera size={32} strokeWidth={1.5} />}
          title="아직 사진이 없습니다"
          description="사진 생성이 완료되면 여기에 표시됩니다"
        />
      ) : (
        <ImageGallery images={galleryImages} columns={3} variant="minimal" />
      )}
    </div>
  )
}
